import React, { useContext, useEffect } from "react";
import { gql } from "@urql/core";
import { SurveyContext, QuestionProps } from "../../survey";
import Checkbox from "../../../components/data_entry/checkbox";
import { MentorChatGroupsDocument } from "../../../generated/graphql";
import QuestionLabel from "../components/question_label";
import nextId from "react-id-generator";
import { useQuery } from "urql";
import { captureError } from "../../../../utils/capture_error";

gql`
  query MentorChatGroups($mentorId: ID!) {
    mentor(id: $mentorId) {
      id
      mentorships {
        id
        isMatched
        chatGroup {
          id
        }
        student {
          id
          user {
            id
            firstName
            lastName
          }
        }
      }
    }
  }
`;

export default function MentorChatGroupStudentSelect({
  customizations,
  currentAnswer,
  updateAnswer,
  setAbleToSubmit,
}: QuestionProps<string[], { prompt: string }>) {
  const { participantId, mentorshipChatGroupId } = useContext(SurveyContext);

  const [{ fetching, error, data }] = useQuery({
    query: MentorChatGroupsDocument,
    variables: { mentorId: participantId },
  });

  // an empty array is still valid here, it just means the user selected "none"
  const answerIsValid = typeof currentAnswer !== "undefined";
  useEffect(() => {
    setAbleToSubmit(answerIsValid);
  }, [answerIsValid, setAbleToSubmit]);

  if (fetching || data === null) return <div>Loading...</div>;
  if (error || !data) {
    const graphQLErrorsForAPM = error?.graphQLErrors?.join("; ") ?? "";
    captureError(new Error(`Error fetching useMentorChatGroupsQuery: ${graphQLErrorsForAPM}`));
    return null;
  }

  const mentorships = data.mentor.mentorships;

  if (!mentorshipChatGroupId) {
    throw new Error("Can only use as part of a mentorship group survey");
  }

  const matchedMentorshipsInChatGroup = mentorships.filter(
    (mentorship) =>
      mentorship.chatGroup &&
      mentorship.chatGroup.id === mentorshipChatGroupId &&
      mentorship.isMatched,
  );

  const label = customizations.prompt;
  const id = "student-select-" + nextId();

  const selectedStudentIds = currentAnswer ? currentAnswer : [];
  return (
    <div className="student-select">
      <QuestionLabel label={label} htmlFor={id} />
      {matchedMentorshipsInChatGroup.map((mentorship) => {
        const student = mentorship.student;
        const user = student.user;

        return (
          <Checkbox
            htmlFor={id}
            key={student.id}
            dataTest="student-select"
            label={`${user.firstName} ${user.lastName}`}
            checked={selectedStudentIds.includes(student.id)}
            onChange={() => {
              const index = selectedStudentIds.indexOf(student.id);
              if (index > -1) {
                updateAnswer([
                  ...selectedStudentIds.slice(0, index),
                  ...selectedStudentIds.slice(index + 1),
                ]);
              } else {
                updateAnswer([...selectedStudentIds, student.id]);
              }
            }}
          />
        );
      })}
      <Checkbox
        label="None"
        htmlFor={id}
        checked={
          !!(
            currentAnswer /* we don't want this checked if currentAnswer isn't set */ &&
            currentAnswer.length === 0
          )
        }
        onChange={() => {
          updateAnswer([]);
        }}
      />
    </div>
  );
}
